import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
  InputAdornment,
  Pagination,
  Grid,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Chip,
  ImageList,
  ImageListItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import Main from '../layouts/Main';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import AddImageDialog from '../components/AddImageDialog';

const ProductImages = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const page = parseInt(searchParams.get('page') || 1);
  const limit = parseInt(searchParams.get('limit') || 25);

  const fetchProductImages = async () => {
    try {
      setLoading(true);
      let url = `https://api.elnaf.fi/api/list_product_images.php?page=${page}&limit=${limit}`;
      if (searchTerm) {
        url += `&q=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      console.log('Product images API response:', data);
      
      setProducts(data.data || []);
      setTotal(data.total || 0);
      const totalPages = Math.ceil((data.total || 0) / (data.limit || limit));
      setPageCount(totalPages);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product images:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductImages();
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    params.set('page', '1');
    params.set('limit', limit.toString());
    setSearchParams(params);
  };

  const handlePageChange = (event, value) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', value.toString());
    setSearchParams(params);
  };

  const handleManageImages = (sku) => {
    navigate(`/product-images/${sku}`);
  };

  const handleAddSave = async () => {
    setAddDialogOpen(false);
    await fetchProductImages();
  };

  return (
    <Main>
      <div className="page-header">
        <div>
          <h1 className="page-title">Product Images</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Product Images</li>
          </ol>
        </div>
        <div className="page-header-actions">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            sx={{ 
              backgroundColor: '#845adf',
              '&:hover': {
                backgroundColor: '#6c48c5'
              }
            }}
          >
            Add Image
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">Product Images by SKU</div>
              {total > 0 && (
                <div className="ms-auto">
                  <Typography variant="body2" color="text.secondary">
                    Total: {total} products
                  </Typography>
                </div>
              )}
            </div>
            <div className="card-body">
              <Box component="form" onSubmit={handleSearch} sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search by SKU..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button type="submit" variant="contained" size="small">
                          Search
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                  <CircularProgress />
                </Box>
              ) : products && products.length > 0 ? (
                <>
                  <Grid container spacing={3}>
                    {products.map((product) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={product.sku}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" gutterBottom>
                              {product.sku}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mb={2}>
                              <ImageIcon color="action" />
                              <Chip
                                label={`${product.image_urls?.length || 0} images`}
                                size="small"
                                color="primary"
                              />
                            </Box>
                            
                            {/* Image preview grid */}
                            {product.image_urls && product.image_urls.length > 0 ? (
                              <Box sx={{ height: 200, overflow: 'hidden' }}>
                                <ImageList sx={{ width: '100%', height: '100%' }} cols={2} gap={4}>
                                  {product.image_urls.slice(0, 4).map((img, index) => (
                                    <ImageListItem key={index}>
                                      <img
                                        src={img.url}
                                        alt={`${product.sku}-${index}`}
                                        loading="lazy"
                                        style={{ 
                                          width: '100%', 
                                          height: '100%', 
                                          objectFit: 'cover',
                                          borderRadius: 4
                                        }}
                                      />
                                    </ImageListItem>
                                  ))}
                                </ImageList>
                                {product.image_urls.length > 4 && (
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary" 
                                    sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                                  >
                                    +{product.image_urls.length - 4} more
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Box 
                                sx={{ 
                                  height: 200, 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  backgroundColor: '#f5f5f5',
                                  borderRadius: 1
                                }}
                              >
                                <Typography color="text.secondary">
                                  No images
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                          <CardActions>
                            <Button 
                              fullWidth
                              variant="outlined"
                              onClick={() => handleManageImages(product.sku)}
                            >
                              Manage Images
                            </Button>
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  
                  {pageCount > 1 && (
                    <Box display="flex" justifyContent="center" mt={3}>
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Box textAlign="center" py={5}>
                  <Typography variant="h6" color="text.secondary">
                    {searchTerm 
                      ? `No products found for SKU: ${searchTerm}`
                      : 'No product images found. Add your first image!'
                    }
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddImageDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleAddSave}
      />
    </Main>
  );
};

export default ProductImages;